import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Download, Globe } from 'lucide-react';
import VideoJSPlayer from './VideoJSPlayer';
import { LanguageSelectionModal } from './LanguageSelectionModal';
import { translateSrt, translateVtt } from '@/utils/apiWithRecaptcha';
import { useRecaptcha } from '@/contexts/RecaptchaContext';
import { useToast } from '@/hooks/use-toast';
import { buildApiUrl } from '@/config/env';

interface FullPlayerProps {
  videoUrl: string;
  videoType?: string;  // 🚀 NEW: MIME type
  srtUrl?: string;
  vttUrl?: string;     // 🚀 NEW: VTT subtitle URL (for Video.js)
  translatedSrtUrl?: string;
  translatedVttUrl?: string; // 🚀 NEW: Translated VTT subtitle URL (for Video.js)
  fallbackVideoUrl?: string; // 🚀 NEW: fallback S3 URL if Object URL fails
  videoData?: any;
  onSubtitleUpdate?: (newSrtUrl: string) => void; // 🚀 NEW: callback to update subtitle
}

export default function FullPlayer({ videoUrl, videoType, srtUrl, vttUrl, translatedSrtUrl, translatedVttUrl, fallbackVideoUrl, videoData, onSubtitleUpdate }: FullPlayerProps) {
  // 🚀 FALLBACK: Use preview VTT if full VTT not available
  const effectiveVttUrl = vttUrl || videoData?.preview?.s3_vtt_url;
  const { t } = useTranslation();
  
  // 🚀 NEW: Translation state
  const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
  const [showStartFromBeginModal, setShowStartFromBeginModal] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState<string>('0');
  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<string>('auto');
  
  // 🚀 FIX: Internal state to track translated URLs for proper re-rendering
  const [currentTranslatedSrtUrl, setCurrentTranslatedSrtUrl] = useState<string | null>(translatedSrtUrl || null);
  const [currentTranslatedVttUrl, setCurrentTranslatedVttUrl] = useState<string | null>(translatedVttUrl || null);
  
  // 🚀 NEW: Track if we've shown the language modal automatically
  const [hasShownLanguageModal, setHasShownLanguageModal] = useState<boolean>(false);
  
  // 🚀 Improved loading UI state
  const [translateProgress, setTranslateProgress] = useState<number>(0);
  const [tipIndex, setTipIndex] = useState<number>(0);
  const loadingTips = [
    'Đang phân tích phụ đề...',
    'Đang dịch theo từng đoạn...',
    'Đang đồng bộ phụ đề...',
    'Đang tối ưu cách ngắt dòng...',
    'Sắp hoàn tất...'
  ];

  const { executeRecaptcha } = useRecaptcha();
  const { toast } = useToast();

  // 🚀 NEW: Ref for language section to enable auto-scroll
  const languageSectionRef = useRef<HTMLDivElement>(null);
  
  // 🚀 NEW: Ref for video player to scroll after translation
  const videoPlayerRef = useRef<HTMLDivElement>(null);

  // 🚀 NEW: Function to scroll to language section
  const scrollToLanguageSection = useCallback(() => {
    if (languageSectionRef.current) {
      const elementTop = languageSectionRef.current.offsetTop;
      const offset = 100; // Add some padding from top
      
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
      
      console.log('📜 FullPlayer - Scrolled to language section for translation visibility');
    }
  }, []);

  // 🚀 NEW: Function to scroll to video player after translation
  const scrollToVideoPlayer = useCallback(() => {
    console.log('📜 FullPlayer - Attempting to scroll to video player...');
    if (videoPlayerRef.current) {
      const elementTop = videoPlayerRef.current.offsetTop;
      const offset = 100; // Same offset as VideoPreview
      
      console.log('📜 FullPlayer - Scrolling to video player position:', elementTop - offset);
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
      
      console.log('📜 FullPlayer - Scrolled to video player for translated subtitles viewing');
    } else {
      console.warn('⚠️ FullPlayer - videoPlayerRef.current is null, cannot scroll');
    }
  }, []);

  // 🚀 NEW: Translation limit state
  const [translationCount, setTranslationCount] = useState<number>(0);
  const [isTranslationLimitReached, setIsTranslationLimitReached] = useState<boolean>(false);

  // 🚀 NEW: Check translation limit on component mount
  React.useEffect(() => {
    if (videoData?.full?.translation_count !== undefined) {
      setTranslationCount(videoData.full.translation_count);
      setIsTranslationLimitReached(videoData.full.translation_count >= 9);
    }
  }, [videoData?.full?.translation_count]);

  // 🚀 NEW: Show language modal when full processing is complete
  React.useEffect(() => {
    const isFullCompleted = videoData?.full?.status === 'completed';
    
    // Show language modal when full is completed and we haven't shown it yet
    if (isFullCompleted && !hasShownLanguageModal && !isTranslationLimitReached) {
      console.log('🌐 FullPlayer - Full processing complete, showing language modal');
      setShowLanguageModal(true);
      setHasShownLanguageModal(true);
    }
  }, [videoData?.full?.status, hasShownLanguageModal, isTranslationLimitReached]);

  // 🚀 NEW: Function to reset video process to not_started
  const resetVideoProcess = async () => {
    try {
      console.log('🔄 FullPlayer - Resetting video process to not_started');
      
      const response = await fetch(`${buildApiUrl('api/reset-video-process')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_id: videoData?.video_id || videoData?.id,
          reset_reason: 'translation_limit_exceeded'
        }),
      });

      if (response.ok) {
        console.log('✅ FullPlayer - Video process reset successfully');
        toast({
          title: "Translation limit exceeded",
          description: "Video process has been reset. Please make payment again to continue.",
          variant: "destructive",
        });
        
        // Trigger page reload or redirect to force re-payment
        window.location.reload();
      } else {
        throw new Error(`Failed to reset video process: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ FullPlayer - Error resetting video process:', error);
      toast({
        title: "Reset failed",
        description: "Please contact support to reset your video process.",
        variant: "destructive",
      });
    }
  };

  // 🚀 NEW: Translation functions
  // 🚀 FIXED: Language selection logic - pass language directly to avoid state timing issues
  const handleLanguageModalConfirm = async (selectedLanguage: string | null) => {
    console.log('🌐 FullPlayer - Language modal confirmed:', { selectedLanguage });
    
    if (!selectedLanguage || selectedLanguage === '0') {
      console.log('⚠️ FullPlayer - No language selected, closing modal');
      setShowLanguageModal(false);
      return;
    }

    console.log('✅ FullPlayer - Setting selected language:', selectedLanguage);
    setSelectedTargetLanguage(selectedLanguage);
    setShowLanguageModal(false);
    
    // 🚀 FIX: Wait for state update before starting translation
    console.log('🌐 FullPlayer - Starting translation with language:', selectedLanguage);
    await startTranslation(selectedLanguage); // Pass language directly
  };

  const startTranslation = async (targetLanguage?: string) => {
    // 🚀 FIX: Use passed language or fallback to state
    const languageToUse = targetLanguage || selectedTargetLanguage;
    
    if (!languageToUse || languageToUse === '0') {
      console.error('❌ FullPlayer - No valid language for translation:', { targetLanguage, selectedTargetLanguage });
      toast({
        title: "Language not selected",
        description: "Please select a target language for translation.",
        variant: "destructive",
      });
      return;
    }

    // 🚀 NEW: Check translation limit before proceeding
    if (isTranslationLimitReached) {
      console.log('🚫 FullPlayer - Translation limit reached, resetting video process');
      toast({
        title: "Translation limit exceeded",
        description: "You have reached the maximum of 9 translations. Video process will be reset.",
        variant: "destructive",
      });
      
      // Reset video process and force re-payment
      await resetVideoProcess();
      return;
    }

    // 🚀 NEW: Check if this would be the 10th translation
    if (translationCount >= 9) {
      console.log('🚫 FullPlayer - Translation limit would be exceeded, resetting video process');
      toast({
        title: "Translation limit exceeded",
        description: "This would be your 10th translation. Video process will be reset.",
        variant: "destructive",
      });
      
      // Reset video process and force re-payment
      await resetVideoProcess();
      return;
    }

    console.log('🌐 FullPlayer - Starting translation with language:', languageToUse, `(Translation ${translationCount + 1}/9)`);
    setIsTranslating(true);
    
    // 🚀 NEW: Auto-scroll to language section to show loading state
    scrollToLanguageSection();
    
    try {
      // Use the current SRT and VTT URLs (either original or full video)
      const currentSrtUrl = srtUrl || videoData?.full?.s3_srt_url;
      const currentVttUrl = vttUrl || videoData?.full?.s3_vtt_url;
      
      if (!currentSrtUrl && !currentVttUrl) {
        throw new Error('No subtitle files available for translation');
      }

      console.log('🌐 FullPlayer - Translation details:', {
        srtUrl: currentSrtUrl,
        vttUrl: currentVttUrl,
        targetLanguage: languageToUse,
        sourceLanguage: selectedSourceLanguage,
        selectedTargetLanguage: selectedTargetLanguage,
        currentTranslationCount: translationCount,
        maxTranslations: 3
      });

      // 🚀 OPTIMIZED: Chỉ gọi 1 API để tiết kiệm chi phí translation
      // Ưu tiên SRT vì API translate-srt trả về cả SRT và VTT
      const currentUrl = currentSrtUrl || currentVttUrl;
      if (!currentUrl) {
        throw new Error('No subtitle files available for translation');
      }

      const translationResult = await translateSrt(
        currentUrl,
        languageToUse,
        selectedSourceLanguage === 'auto' ? 'auto' : selectedSourceLanguage,
        videoData?.videoId,
        executeRecaptcha
      );
      
      console.log('✅ FullPlayer - Translation completed:', translationResult);
      
      // 🚀 NEW: Update translation count
      const newTranslationCount = translationCount + 1;
      setTranslationCount(newTranslationCount);
      
      // 🚀 NEW: Check if limit reached after this translation
      if (newTranslationCount >= 3) {
        setIsTranslationLimitReached(true);
        console.log('🚫 FullPlayer - Translation limit reached after this translation');
      }
      
      // 🚀 Extract both SRT and VTT URLs from single API response
      const translatedSrtUrl = translationResult.translated_srt_url || null;
      const translatedVttUrl = translationResult.translated_vtt_url || null;

      // 🚀 FIX: Update internal state to trigger VideoJSPlayer re-render
      setCurrentTranslatedSrtUrl(translatedSrtUrl);
      setCurrentTranslatedVttUrl(translatedVttUrl);

      // Update subtitle in VideoJSPlayer if callback is provided (prioritize VTT)
      if (onSubtitleUpdate) {
        if (translatedVttUrl) {
          onSubtitleUpdate(translatedVttUrl); // VTT for Video.js
        } else if (translatedSrtUrl) {
          onSubtitleUpdate(translatedSrtUrl); // SRT fallback
        }
      }

      const translatedFormats = [];
      if (translatedSrtUrl) translatedFormats.push('SRT');
      if (translatedVttUrl) translatedFormats.push('VTT');

      toast({
        title: "Translation completed!",
        description: `Subtitles translated to ${languageToUse} in ${translatedFormats.join(' & ')} format with context-aware technology for natural movie dialogue (${newTranslationCount}/3 translations used)`,
        variant: "default",
      });

      // 🚀 NEW: Scroll to video player after translation completes
      setTimeout(() => {
        scrollToVideoPlayer();
      }, 500); // Delay to ensure UI updates first

    } catch (error) {
      console.error('❌ FullPlayer - Translation error:', error);
      toast({
        title: "Translation failed",
        description: error instanceof Error ? error.message : "Failed to translate subtitles",
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const openLanguageModal = () => {
    console.log('🌐 FullPlayer - Opening language modal for translation');
    setShowLanguageModal(true);
  };

  // 🚀 Fix: Priority for full video - translated SRT > full video SRT > original SRT > preview SRT
  const effectiveSrtUrl = translatedSrtUrl || 
                          videoData?.full?.s3_srt_url ||  // 🚀 Full video SRT first
                          srtUrl ||                                  // Original SRT
                          videoData?.preview?.s3_srt_url;       // Preview SRT last
  
  // 🚀 NEW: Clear distinction between original and translated subtitles
  const originalSrtUrl = videoData?.full?.s3_srt_url || srtUrl; // Original SRT (full video or fallback)
  const translatedSrtUrlFinal = translatedSrtUrl; // Translated SRT (if available)
  
  console.log('🎬 FullPlayer - SRT URLs (Fixed Priority):', {
    translatedSrtUrl,
    fullVideoSrt: videoData?.full?.s3_srt_url,
    originalSrt: srtUrl,
    previewSrt: videoData?.preview?.s3_srt_url,
    effectiveSrtUrl,
    originalSrtUrl,
    translatedSrtUrlFinal
  });

  React.useEffect(() => {
    if (!isTranslating) {
      setTipIndex(0);
      setTranslateProgress(prev => (prev > 0 ? 100 : 0));
      return;
    }
    setTranslateProgress(0);
    let progress = 0;
    const progressTimer = setInterval(() => {
      const increment = progress < 70 ? Math.random() * 7 + 3 : progress < 90 ? Math.random() * 3 + 1 : Math.random() * 1;
      progress = Math.min(progress + increment, 95);
      setTranslateProgress(Math.round(progress));
    }, 400);
    const tipTimer = setInterval(() => {
      setTipIndex(prev => (prev + 1) % loadingTips.length);
    }, 1800);
    return () => { clearInterval(progressTimer); clearInterval(tipTimer); };
  }, [isTranslating]);

  return (
          <div className="w-full space-y-6">
      {/* Video Player */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Full Video Player
            {translatedSrtUrl && (
              <span className="ml-2 text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                Translated Subtitles
              </span>
            )}
          </h3>
        </div>
        
        <div ref={videoPlayerRef} className="relative">
          <VideoJSPlayer
            videoUrl={videoUrl}
            videoType={fallbackVideoUrl ? undefined : videoType}  // 🚀 FIX: Don't pass videoType when fallback MP4 is available
            srtUrl={effectiveSrtUrl}
            vttUrl={effectiveVttUrl} // 🚀 FALLBACK: Use preview VTT if full not available
            originalSrtUrl={originalSrtUrl} // 🚀 NEW: Original subtitle URL (full video SRT)
            translatedSrtUrl={currentTranslatedSrtUrl} // 🚀 FIX: Use internal state for proper re-render
            translatedVttUrl={currentTranslatedVttUrl} // 🚀 FIX: Use internal state for proper re-render
            fallbackVideoUrl={fallbackVideoUrl} // 🚀 NEW: S3 fallback URL
            onReady={() => console.log('🎬 VideoJSPlayer ready')}
            onError={(error: any) => console.error('❌ VideoJSPlayer error:', error)}
          />
        </div>
      </div>

      {/* Download Section */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Download Options</h3>
        
        {/* 🚀 IMPORTANT: Download buttons in grid, Translation button on separate line */}
        <div className={`grid gap-4 ${
          srtUrl && translatedSrtUrl 
            ? 'grid-cols-1 md:grid-cols-2' 
            : 'grid-cols-1'
        }`}>
          {/* Original SRT */}
          {srtUrl && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Original Subtitles (SRT)</h4>
              <p className="text-sm text-gray-600 mb-3">Download the original generated subtitles</p>
              <button
                onClick={() => window.open(srtUrl, '_blank')}
                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2 inline" />
                Download Original SRT
              </button>
            </div>
          )}

          {/* Translated SRT */}
          {translatedSrtUrl && (
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <h4 className="font-medium text-gray-900 mb-2">Translated Subtitles (SRT)</h4>
              <p className="text-sm text-gray-600 mb-3">Download the translated subtitles</p>
              <button
                onClick={() => window.open(translatedSrtUrl, '_blank')}
                className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2 inline" />
                Download Translated SRT
              </button>
            </div>
          )}

          {/* 🚀 NEW: Original VTT */}
          {vttUrl && (
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
              <h4 className="font-medium text-gray-900 mb-2">Original Subtitles (VTT)</h4>
              <p className="text-sm text-gray-600 mb-3">Download the original subtitles in VTT format</p>
              <button
                onClick={() => window.open(vttUrl, '_blank')}
                className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2 inline" />
                Download Original VTT
              </button>
            </div>
          )}

          {/* 🚀 NEW: Translated VTT */}
          {translatedVttUrl && (
            <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50">
              <h4 className="font-medium text-gray-900 mb-2">Translated Subtitles (VTT)</h4>
              <p className="text-sm text-gray-600 mb-3">Download the translated subtitles in VTT format</p>
              <button
                onClick={() => window.open(translatedVttUrl, '_blank')}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2 inline" />
                Download Translated VTT
              </button>
            </div>
          )}
        </div>

        {/* 🚀 SEPARATE LINE: Translation button is displayed on its own row */}
        {/* This button is displayed REGARDLESS of whether user chose translation in step 1 */}
        {/* User can always translate subtitles to another language at any time */}
        <div ref={languageSectionRef} className="mt-6 border border-orange-200 rounded-lg p-4 bg-orange-50">
          <h4 className="font-medium text-gray-900 mb-2">🌐 Translation Service</h4>
          <p className="text-sm text-gray-600 mb-3">
            Get subtitles in your preferred language
            {translationCount > 0 && (
              <span className="ml-2 text-orange-600 font-medium">
                ({translationCount}/9 translations used)
              </span>
            )}
          </p>
          
          {isTranslationLimitReached ? (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700 font-medium">
                  🚫 Translation limit reached (9/9)
                </p>
                <p className="text-xs text-red-600 mt-1">
                  You have used all 9 translations. Video process will be reset.
                </p>
              </div>
              <button
                onClick={resetVideoProcess}
                className="w-full px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
              >
                🔄 Reset Video Process & Pay Again
              </button>
            </div>
          ) : (
            <button
              onClick={openLanguageModal}
              disabled={isTranslating}
              className="w-full px-4 py-3 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTranslating ? (
                <>
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] text-white/90">🔄 Đang dịch phụ đề...</span>
                      <span className="text-[12px] text-white/90">{translateProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/30 rounded-full overflow-hidden">
                      <div className="h-2 bg-white" style={{ width: `${translateProgress}%` }} />
                    </div>
                    <p className="text-[11px] text-white/90 mt-1">{loadingTips[tipIndex]}</p>
                  </div>
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2 inline" />
                  Translate to Another Language
                </>
              )}
            </button>
          )}
        </div>

        {/* 🚀 NEW: Start with another file button - BELOW Translation Service */}
        <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-2">🔄 Start Over</h4>
          <p className="text-sm text-gray-600 mb-3">
            Upload and process a different video or audio file
          </p>
          <button
            onClick={() => setShowStartFromBeginModal(true)}
            className="w-full px-4 py-3 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition-colors"
          >
            🚀 Start with another file
          </button>
        </div>


      </div>

      {/* 🚀 NEW: Language Selection Modal */}
      <LanguageSelectionModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        onConfirm={handleLanguageModalConfirm}
        fileName={videoData?.original_name || 'video'}
      />

      {/* Modal xác nhận start from begin */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${showStartFromBeginModal ? 'block' : 'hidden'}`}>
        <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-[calc(50%-500px)] md:-translate-y-[calc(50%-780px)] bg-white rounded-lg p-6 w-[calc(90vw-32px)] max-w-2xl shadow-xl">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-xl md:text-lg font-semibold text-gray-900">Start from beginning?</h3>
              <p className="text-base md:text-sm text-gray-600">This will reset your current progress</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <span className="text-base md:text-sm text-yellow-800 font-medium">
                Warning: Starting over will reset all progress.
              </span>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setShowStartFromBeginModal(false)}
              className="flex-1 px-4 py-2 md:px-3 py-3 md:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-base md:text-sm"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowStartFromBeginModal(false);
                window.location.href = '/';
              }}
              className="flex-1 px-4 py-2 md:px-3 py-3 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-base md:text-sm"
            >
              Yes, Start Over
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
